import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import DarkModeToggle from './components/DarkModeToggle';
import Footer from './components/Footer';
import BackgroundTexture from './components/BackgroundTexture';
import CookieConsent from './components/CookieConsent';
import titleUtils from './utils/titleUtils';

const LegalPage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { titleFontSize, isMobile } = titleUtils();
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        window.scrollTo(0, 0);
      }
      setIsLoaded(true);
    }, 100);
  }, []);

  return (
    <div className="relative z-0">
      <BackgroundTexture
        className="background-texture"
        zIndex={-1}
        patternType="geometric"
      />
      <div
        className={`min-h-screen grid grid-rows-[auto_1fr_auto]  transition-colors duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Contenedor del contenido principal */}
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="max-w-7xl mx-auto">
            <header
              className={`flex justify-between items-center mb-6 sm:mb-8 transition-all duration-300 ${
                isLoaded
                  ? 'translate-y-0 opacity-100'
                  : '-translate-y-10 opacity-0'
              } relative`}
            >
              <Link to="/" className="group">
                <div className="icon-bg">
                  <ArrowLeft className="text-white w-1/2 h-1/2" />
                </div>
              </Link>
              <h1
                className={`font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-red-500 leading-tight absolute left-1/2 -translate-x-1/2 ${titleFontSize}`}
              >
                Legal
              </h1>
              <DarkModeToggle />
            </header>

            <div
              className={`bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden transition-all duration-300 ${
                isLoaded
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-10 opacity-0'
              }`}
            >
              <div className="p-4 sm:p-6 relative">
                {/* Avís Legal i Identificació */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Avís Legal i Identificació
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  <strong>minijocs.cat</strong> és una plataforma de minijocs en
                  línia. L'ús d'aquest lloc web implica l'acceptació dels
                  nostres termes i condicions. Aquest avís legal regula l'ús del
                  servei del portal web <strong>minijocs.cat</strong> que posem
                  a disposició dels usuaris d'Internet.
                </p>

                {/* Propietat Intel·lectual */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Propietat Intel·lectual i Industrial
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Tots els drets de propietat intel·lectual i industrial de{' '}
                  <strong>minijocs.cat</strong>, incloent-hi els elements que
                  componen el disseny gràfic, el codi font, els logotips,
                  textos, gràfics, il·lustracions, fotografies i altres elements
                  que apareixen al lloc web, són propietat de{' '}
                  <strong>minijocs.cat</strong> o dels seus llicenciadors.
                </p>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  No està permesa la reproducció total o parcial d'aquest lloc
                  web, ni de cap dels seus continguts, sense l'autorització
                  expressa de <strong>minijocs.cat</strong>.
                </p>

                {/* Política de Privacitat */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Política de Privacitat
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Respectem la vostra privacitat i complim amb la normativa
                  vigent de protecció de dades. La informació personal que ens
                  proporcioneu serà tractada amb confidencialitat i seguretat.
                </p>
                <ul className="list-disc pl-5 mb-4 text-gray-600 dark:text-gray-300">
                  <li className="mb-2">
                    <strong>Responsable:</strong> minijocs.cat
                  </li>
                  <li className="mb-2">
                    <strong>Dades que recollim:</strong>
                    <ul className="list-disc pl-5 mt-2">
                      <li>Nom d'usuari o àlies</li>
                      <li>Dades de joc (puntuacions, nivells)</li>
                      <li>Informació tècnica (IP, navegador)</li>
                      <li>Dades d'anàlisi web</li>
                    </ul>
                  </li>
                  <li className="mb-2">
                    <strong>Finalitat:</strong> Gestionar la vostra participació
                    en els minijocs, mantenir les puntuacions i estadístiques, i
                    millorar l'experiència d'usuari.
                  </li>
                  <li className="mb-2">
                    <strong>Drets:</strong> Podeu exercir els vostres drets
                    d'accés, rectificació, supressió, limitació, portabilitat i
                    oposició contactant amb nosaltres.
                  </li>
                </ul>

                {/* Eines d'Anàlisi */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Eines d'Anàlisi
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Utilitzem eines d'anàlisi per millorar els nostres serveis.
                  Aquestes eines utilitzen cookies per recopilar informació
                  sobre com utilitzeu el nostre lloc web. La informació
                  recollida inclou:
                </p>
                <ul className="list-disc pl-5 mb-4 text-gray-600 dark:text-gray-300">
                  <li>Pàgines visitades i temps de permanència</li>
                  <li>Tipus de dispositiu i navegador utilitzat</li>
                  <li>País i ciutat (sense geolocalització precisa)</li>
                  <li>Interaccions amb elements de la pàgina</li>
                </ul>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Les dades són processades de forma anònima i agregada. Podeu
                  desactivar l'ús d'aquestes eines mitjançant la configuració de
                  cookies.
                </p>

                {/* Política de Cookies */}
                <h2
                  id="cookies"
                  className="text-xl font-bold mb-4 text-gray-800 dark:text-white"
                >
                  Política de Cookies
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Utilitzem cookies per millorar la vostra experiència. Les
                  cookies són petits arxius de text que s'emmagatzemen al vostre
                  dispositiu. Utilitzem els següents tipus:
                </p>
                <ul className="list-disc pl-5 mb-4 text-gray-600 dark:text-gray-300">
                  <li className="mb-2">
                    <strong>Cookies tècniques essencials:</strong> Necessàries
                    per al funcionament bàsic del lloc web. No es poden
                    desactivar.
                  </li>
                  <li className="mb-2">
                    <strong>Cookies analítiques:</strong> Per analitzar el
                    tràfic i l'ús del lloc web.
                  </li>
                  <li className="mb-2">
                    <strong>Cookies de funcionalitat:</strong> Per recordar les
                    vostres preferències i millorar la vostra experiència.
                  </li>
                </ul>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Podeu gestionar o desactivar les cookies no essencials
                  mitjançant la configuració del vostre navegador.
                </p>

                {/* Condicions d'Ús */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Condicions d'Ús
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  En utilitzar <strong>minijocs.cat</strong>, accepteu complir
                  amb les següents condicions:
                </p>
                <ul className="list-disc pl-5 mb-4 text-gray-600 dark:text-gray-300">
                  <li>Utilitzar el lloc web de manera responsable i legal</li>
                  <li>No intentar accedir a àrees restringides del lloc web</li>
                  <li>No interferir amb el funcionament normal del lloc web</li>
                  <li>
                    No utilitzar sistemes automatitzats per accedir al lloc web
                  </li>
                  <li>
                    No realitzar activitats que puguin danyar o perjudicar el
                    lloc web
                  </li>
                </ul>

                {/* Legislació */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Legislació Aplicable
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Aquestes condicions es regeixen per la legislació espanyola.
                  Per a la resolució de qualsevol controvèrsia que pogués sorgir
                  de l'accés o ús d'aquest lloc web, les parts se sotmeten als
                  Jutjats i Tribunals competents.
                </p>

                {/* Compromís amb Famílies */}
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                  Compromís amb les Famílies
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  Tot el contingut està dissenyat per ser apte per a totes les
                  edats. Ens comprometem a mantenir un entorn segur i adequat
                  per a tots els usuaris, especialment per als menors. Prenem
                  mesures per protegir la privacitat i la seguretat dels menors
                  que utilitzen la nostra plataforma.
                </p>

                {/* Separador */}
                <hr className="my-6 border-gray-300 dark:border-gray-700" />

                {/* Texto de Actualización */}
                <div className="text-right text-[10px] xs:text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Última actualització: Març 2025
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <CookieConsent />
    </div>
  );
};

export default LegalPage;
